import { useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';

import { AsyncSelect, type BaseAsyncSelectProps } from '@/components/common/select/AsyncSelect';
import { useCurrency } from '@/hooks/use-currency';
import { useFormatCurrency } from '@/hooks/use-format-currency';

export type CurrencyOption = {
  id: string;
  name: string;
};

type CurrencySelectProps = BaseAsyncSelectProps<CurrencyOption> & {
  noAuth?: boolean;
  withEmpty?: boolean;
  availableCurrency?: boolean;
};

export const CurrencySelect: FC<CurrencySelectProps> = (props) => {
  const t = useTranslations('misc.select.currency');

  const {
    placeholder = t('placeholder'),
    emptyListNode = t('noOptionsMessage'),
    noAuth,
    withEmpty,
    availableCurrency = true,
    ...selectProps
  } = props;

  const { loading, data } = useCurrency(availableCurrency, noAuth);

  const formatCurrency = useFormatCurrency();

  const options = useMemo(
    () =>
      [...(withEmpty ? [{ id: '' }] : []), ...(data ?? [])].map((currency) => ({
        id: currency.id,
        name: formatCurrency(currency),
      })),
    [data, formatCurrency, withEmpty]
  );

  return (
    <AsyncSelect
      loading={loading}
      options={options}
      placeholder={placeholder}
      emptyListNode={emptyListNode}
      {...selectProps}
    />
  );
};
